import { useEffect } from 'react';

import { useQueryInvitationUrl } from './useQueryInvitationUrl';
import { useCreateInvitationUrl } from './useCreateInvitationUrl';
import { InvitationUrlsStatus } from './__types__/CreateInvitationUrlQuery';

/**
 * Custom hook that handles fetching and creating invitation links to be used
 * in the invite page.
 *
 * @returns An object containing the invite link, an optional error, and loading state.
 */

type UseInviteLinkProps = { skip?: boolean; allowGenerateNewLink?: boolean };

export const useInviteLink = ({ skip, allowGenerateNewLink }: UseInviteLinkProps) => {
	const {
		existingInviteLink,
		existingInviteLinkStatus,
		loading: queryInviteLoading,
		error: queryInviteError,
	} = useQueryInvitationUrl({ skipQuery: skip });

	const {
		createNewInviteLink,
		newInviteLink,
		error: createInviteError,
		loading: createInviteLoading,
		called: createInviteCalled,
	} = useCreateInvitationUrl();

	useEffect(() => {
		// If we have an existing invite link (after fetching) and if it is expired, create a new one
		if (
			!skip &&
			!queryInviteLoading &&
			!queryInviteError &&
			((allowGenerateNewLink && !existingInviteLink) ||
				existingInviteLinkStatus === InvitationUrlsStatus.EXPIRED) &&
			!createInviteCalled
		) {
			void createNewInviteLink();
		}
	}, [
		skip,
		queryInviteLoading,
		createNewInviteLink,
		createInviteCalled,
		queryInviteError,
		allowGenerateNewLink,
		existingInviteLink,
		existingInviteLinkStatus,
	]);

	const inviteLink =
		(existingInviteLinkStatus === InvitationUrlsStatus.ACTIVE && existingInviteLink) ||
		newInviteLink;

	const error = queryInviteError || createInviteError;

	const loading = queryInviteLoading || createInviteLoading;

	return { inviteLink, error, loading };
};
